'use client';

import { useEffect } from 'react';

import { useCustomLogos } from '@/features/marketing/hooks/use-custom-logos';
import { usePublicPricing } from '@/features/marketing/hooks/use-public-pricing';
import { useTemporarySignup } from '@/features/marketing/hooks/use-temporary-signup';
import type { TemporarySignupAction } from '@/features/marketing/lib/marketing-constants';
import { useUtmStorage } from '@/features/marketing/hooks/use-utm-storage';
import {
  initLazyImages,
  playGifById,
} from '@/features/marketing/lib/init-lazy-images';
import { initTestimonialCarousel } from '@/features/marketing/lib/init-testimonial-carousel';

export function UaeClientBridge() {
  const { createTemporaryUser } = useTemporarySignup();
  const { changePlanDuration, fetchPricing } = usePublicPricing();
  useUtmStorage();
  useCustomLogos();

  useEffect(() => {
    void fetchPricing();

    const win = window as typeof window & {
      createTemporaryUser?: (
        action?: string,
        templateId?: string,
        buttonElement?: HTMLElement | null,
      ) => void;
      changePlanDuration?: (duration: string, event?: Event) => void;
      playGIF?: (gifId: string) => void;
    };

    win.createTemporaryUser = (
      action?: string,
      templateId = '',
      buttonElement?: HTMLElement | null,
    ) => {
      void createTemporaryUser(
        (action === 'analyse' ? 'analyse' : 'import') as TemporarySignupAction,
        templateId || '',
        buttonElement ?? null,
      );
    };

    win.changePlanDuration = (duration: string) => {
      if (
        duration === 'monthly' ||
        duration === 'quarterly' ||
        duration === 'yearly'
      ) {
        changePlanDuration(duration);
      }
    };

    win.playGIF = playGifById;

    const cleanupLazy = initLazyImages();
    const cleanupCarousel = initTestimonialCarousel(document, {
      autoplayMs: 6500,
    });

    return () => {
      delete win.createTemporaryUser;
      delete win.changePlanDuration;
      delete win.playGIF;
      cleanupLazy();
      cleanupCarousel();
    };
  }, [changePlanDuration, createTemporaryUser, fetchPricing]);

  return null;
}
